// Shared per-ASIN FC coverage table (Task 11): used both by the War Room row-expander drawer
// (AsinHistoryPanel) and the Shipment Planner per-ASIN detail drawer. Presentational only — the
// caller fetches GET /api/war-room/fc-analysis/{asin} and passes the resulting rows + status
// down. dwCov1dPct/dwCov2dPct are optional (the scorecard row's dw_cov_1d_pct/dw_cov_2d_pct) and
// the "DW coverage" header line is simply omitted when they aren't supplied. dwCov1dEffPct/
// dwCov2dEffPct (dw_cov_1d_eff_pct/dw_cov_2d_eff_pct, Task 13) are likewise optional -- when
// present, the header appends a persistence-discounted "effective" reading alongside the raw one.
import React from 'react';

const ROLE_BADGE_STYLE = {
    THIN_HUB: 'bg-amber-100 text-amber-800 border-amber-300',
    SURPLUS: 'bg-gray-100 text-gray-600 border-gray-300',
};

const ROLE_LABEL = {
    THIN_HUB: 'thin hub',
    SURPLUS: 'surplus',
};

// Task 17 (owner doctrine 2026-08-15): persistence < 50% (thin -- current stock barely dents the
// 45-day saturation horizon) is red, 50-99% (partially saturated) is amber, 100% (fully saturated,
// clamped) is green.
const PERSISTENCE_RED_MAX = 50;
const PERSISTENCE_AMBER_MAX = 100;

const fmtNum = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString());
const fmtPct = (v) => (v === null || v === undefined ? '—' : `${v}%`);
const fmtDemandMo = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(1));
const fmtRunwayDays = (v) => (v === null || v === undefined ? '∞' : `${Number(v).toFixed(1)}d`);

function persistenceClass(pct) {
    if (pct < PERSISTENCE_RED_MAX) return 'text-red-600 font-medium';
    if (pct < PERSISTENCE_AMBER_MAX) return 'text-amber-600 font-medium';
    return 'text-green-700 font-medium';
}

function PersistenceCell({ persistence }) {
    if (persistence === null || persistence === undefined) {
        return <span>—</span>;
    }
    const pct = Number(persistence) * 100;
    return <span className={persistenceClass(pct)}>{Math.round(pct)}%</span>;
}

function RoleBadge({ role }) {
    if (!role || role === 'OK') return null;
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${ROLE_BADGE_STYLE[role] || 'bg-gray-100 text-gray-600 border-gray-300'}`}>
            {ROLE_LABEL[role] || role.toLowerCase()}
        </span>
    );
}

function DwCoverageHeader({ dwCov1dPct, dwCov2dPct, dwCov1dEffPct, dwCov2dEffPct }) {
    if (dwCov1dPct === null || dwCov1dPct === undefined || dwCov2dPct === null || dwCov2dPct === undefined) {
        return null;
    }
    const hasEff = dwCov1dEffPct !== null && dwCov1dEffPct !== undefined
        && dwCov2dEffPct !== null && dwCov2dEffPct !== undefined;
    return (
        <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 border-b border-gray-200 bg-gray-100">
            DW coverage: {dwCov1dPct}% 1d &middot; {dwCov2dPct}% 2d
            {hasEff && <> (effective: {dwCov1dEffPct}% &middot; {dwCov2dEffPct}%)</>}
        </div>
    );
}

function FcCoverageTable({ status, rows, dwCov1dPct, dwCov2dPct, dwCov1dEffPct, dwCov2dEffPct }) {
    if (status === 'loading') {
        return (
            <div className="flex items-center justify-center py-6 text-gray-400">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="ml-2 text-sm">Loading FC coverage…</span>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="py-4 text-center text-sm text-red-600">
                Failed to load FC coverage. Please try again.
            </div>
        );
    }

    const sorted = (rows || []).slice().sort((a, b) => (b.reach_2d ?? -1) - (a.reach_2d ?? -1));

    if (sorted.length === 0) {
        return (
            <div className="bg-gray-50 border border-gray-200 rounded-md">
                <DwCoverageHeader
                    dwCov1dPct={dwCov1dPct} dwCov2dPct={dwCov2dPct}
                    dwCov1dEffPct={dwCov1dEffPct} dwCov2dEffPct={dwCov2dEffPct}
                />
                <div className="py-4 text-center text-sm text-gray-500">No FC analysis yet (runs nightly).</div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 border border-gray-200 rounded-md overflow-x-auto">
            <DwCoverageHeader
                dwCov1dPct={dwCov1dPct} dwCov2dPct={dwCov2dPct}
                dwCov1dEffPct={dwCov1dEffPct} dwCov2dEffPct={dwCov2dEffPct}
            />
            <table className="min-w-full divide-y divide-gray-200 text-xs">
                <thead className="bg-gray-100">
                    <tr className="text-left font-semibold text-gray-500 uppercase tracking-wide">
                        <th className="px-3 py-1.5">FC</th>
                        <th className="px-3 py-1.5">City/ST</th>
                        <th className="px-3 py-1.5 text-center">Units</th>
                        <th className="px-3 py-1.5 text-center">Reach 1d</th>
                        <th className="px-3 py-1.5 text-center">Reach 2d</th>
                        <th className="px-3 py-1.5 text-center">Demand %</th>
                        <th className="px-3 py-1.5 text-center">Sug 45d</th>
                        <th className="px-3 py-1.5 text-center">Local demand/mo</th>
                        <th className="px-3 py-1.5 text-center">Runway</th>
                        <th className="px-3 py-1.5 text-center">Persistence</th>
                        <th className="px-3 py-1.5">Role</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                    {sorted.map((row) => (
                        <tr key={row.fc_code}>
                            <td className="px-3 py-1.5 font-mono font-medium text-gray-700">{row.fc_code}</td>
                            <td className="px-3 py-1.5">{row.fc_city ? `${row.fc_city}, ${row.fc_state || ''}` : '—'}</td>
                            <td className="px-3 py-1.5 text-center">
                                {fmtNum(row.units)}
                                {row.in_transit_units > 0 && (
                                    <span className="ml-1 text-amber-600" title={`${row.in_transit_units} unit${row.in_transit_units === 1 ? '' : 's'} in transit (amazon_shipment2)`}>
                                        +{row.in_transit_units} inbound
                                    </span>
                                )}
                            </td>
                            <td className="px-3 py-1.5 text-center">{fmtNum(row.reach_1d)}</td>
                            <td className="px-3 py-1.5 text-center">{fmtNum(row.reach_2d)}</td>
                            <td className="px-3 py-1.5 text-center">{fmtPct(row.marginal_demand_pct)}</td>
                            <td className="px-3 py-1.5 text-center">{fmtNum(row.suggested_units_45d)}</td>
                            <td className="px-3 py-1.5 text-center">{fmtDemandMo(row.local_demand_mo)}</td>
                            <td className="px-3 py-1.5 text-center">{fmtRunwayDays(row.local_runway_days)}</td>
                            <td className="px-3 py-1.5 text-center"><PersistenceCell persistence={row.persistence} /></td>
                            <td className="px-3 py-1.5"><RoleBadge role={row.role_flag} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default FcCoverageTable;
